/**
 * TanStack Router — warehouse-only.
 *
 * Изолированный routeTree, содержит ТОЛЬКО warehouse-страницы +
 * auth (login/reset/profile). Никаких пересечений с router.tsx
 * (дашборд) или router.messenger.tsx (если появится). Tree-shaking
 * выкидывает OverviewPage/ManagersPage/FinancePage и т.п. из bundle.
 *
 * Гарда — protectedRoute с AuthGate (= same logic как в основном router.tsx).
 * После логина — Navigate to "/" → WarehousePage (Сводка склада).
 */
import {
  Outlet,
  Navigate,
  createRootRouteWithContext,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";

import { WarehouseShell } from "@/components/WarehouseShell";
import { useAuth, hasAccess } from "@/auth/AuthContext";
import { LoginPage } from "@/pages/LoginPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { WarehousePage } from "@/pages/WarehousePage";
import { WarehouseStocksPage } from "@/pages/WarehouseStocksPage";
import { WarehouseItemsPage } from "@/pages/WarehouseItemsPage";
import { WarehouseItemsHistoryPage } from "@/pages/WarehouseItemsHistoryPage";
import { WarehouseKitsPage } from "@/pages/WarehouseKitsPage";
import { WarehousePlanningPage } from "@/pages/WarehousePlanningPage";
import { WarehousePurchaseOrdersPage } from "@/pages/WarehousePurchaseOrdersPage";
import { WarehouseSalesOrdersPage } from "@/pages/WarehouseSalesOrdersPage";
import { WarehouseStocktakesPage } from "@/pages/WarehouseStocktakesPage";
import { WarehouseItemCardPage } from "@/pages/WarehouseItemCardPage";
import { WarehouseContractorsPage } from "@/pages/WarehouseContractorsPage";
import { WarehouseDocumentsPage } from "@/pages/WarehouseDocumentsPage";
import { WarehouseInflowFormPage } from "@/pages/WarehouseInflowFormPage";
import { WarehouseOutflowFormPage } from "@/pages/WarehouseOutflowFormPage";
import {
  WarehouseInflowsPage,
  WarehouseOutflowsPage,
} from "@/pages/WarehouseMovementsPage";
import { WarehouseCartProvider } from "@/state/warehouseCart";


interface RouterContext {
  queryClient: QueryClient;
}


function AuthGate() {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <div className="muted">Проверка сессии…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // Склад — только Отдел снабжения (или owner/admin/superuser).
  if (!hasAccess(user, "supply")) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Доступ ограничен</div>
          <div style={{ color: "var(--mute)", marginBottom: 16 }}>
            Раздел Склад доступен только сотрудникам Отдела снабжения.
            Обратитесь к администратору, если вам нужен доступ.
          </div>
          <a href="/messenger" className="btn btn-primary">Перейти в Мессенджер</a>
        </div>
      </div>
    );
  }
  return (
    <WarehouseCartProvider>
      <WarehouseShell />
    </WarehouseCartProvider>
  );
}


function GuestGate() {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user) return <Navigate to="/" replace />;
  return <LoginPage />;
}


const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: GuestGate,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: AuthGate,
});

const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: WarehousePage,
});

const stocksRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/stocks",
  component: WarehouseStocksPage,
});

const inflowsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/inflows",
  component: WarehouseInflowFormPage,
});

const inflowsHistoryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/inflows/history",
  component: WarehouseInflowsPage,
});

const outflowsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/outflows",
  component: WarehouseOutflowFormPage,
});

const outflowsHistoryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/outflows/history",
  component: WarehouseOutflowsPage,
});

const itemsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/items",
  component: WarehouseItemsPage,
});

const itemsHistoryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/items/history",
  component: WarehouseItemsHistoryPage,
});

const kitsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/kits",
  component: WarehouseKitsPage,
});

const planningRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/planning",
  component: WarehousePlanningPage,
});

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/purchase-orders",
  component: WarehousePurchaseOrdersPage,
});

const salesOrdersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/sales-orders",
  component: WarehouseSalesOrdersPage,
});

const stocktakesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/stocktakes",
  component: WarehouseStocktakesPage,
});

const itemCardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/items/$itemId/card",
  component: WarehouseItemCardPage,
});

const contractorsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/contractors",
  component: WarehouseContractorsPage,
});

const documentsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/documents",
  component: WarehouseDocumentsPage,
});

const profileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile",
  component: ProfilePage,
});

const profileViewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile/$userId",
  component: ProfilePage,
});


const routeTree = rootRoute.addChildren([
  loginRoute,
  resetPasswordRoute,
  protectedRoute.addChildren([
    indexRoute,
    stocksRoute,
    inflowsRoute,
    inflowsHistoryRoute,
    outflowsRoute,
    outflowsHistoryRoute,
    itemsRoute,
    itemsHistoryRoute,
    kitsRoute,
    planningRoute,
    purchaseOrdersRoute,
    salesOrdersRoute,
    stocktakesRoute,
    itemCardRoute,
    contractorsRoute,
    documentsRoute,
    profileRoute,
    profileViewRoute,
  ]),
]);


export function buildWarehouseRouter(queryClient: QueryClient) {
  const rawBase = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? "/";
  const basepath = rawBase === "/" ? undefined : rawBase.replace(/\/$/, "");
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    basepath,
  });
}

// NB: declare module Register живёт в router.tsx (общий проект). Здесь
// повторно declare'ить нельзя — TS-конфликт между двумя routeTree.
// Runtime от этого не страдает: warehouse-router всё равно работает
// (просто без type-narrowing для `to`).
